import { Link, useLocation } from 'react-router-dom'
import { Moon, Sun } from 'lucide-react'
import { LogoWordmark } from '@/components/brand/LogoWordmark'
import { SocialIcon, type SocialNetwork } from '@/components/brand/SocialIcon'
import { contactInfo } from '@/data/about'
import { InteractiveDots } from '@/components/motion'

const footerLinks = {
  platform: {
    title: 'Platform',
    links: [
      { label: 'AI Document Generation', href: '/platform/ai-generation' },
      { label: 'Template Management', href: '/platform/templates' },
      { label: 'Brand Asset Management', href: '/platform/dam' },
      { label: 'Excel Intelligence', href: '/platform/excel' },
      { label: 'Productivity Tools', href: '/platform/productivity' },
      { label: 'Security', href: '/platform/security' },
      { label: 'Integrations', href: '/platform/integrations' },
      { label: 'Analytics', href: '/platform/analytics' },
    ],
  },
  solutions: {
    title: 'Solutions',
    links: [
      { label: 'Sales Teams', href: '/solutions/sales' },
      { label: 'Marketing & Brand', href: '/solutions/marketing' },
      { label: 'Operations & Admin', href: '/solutions/operations' },
      { label: 'Finance & Accounting', href: '/solutions/finance' },
    ],
  },
  company: {
    title: 'Company',
    links: [
      { label: 'About Us', href: '/about' },
      { label: 'Contact', href: '/contact' },
      { label: 'Pricing', href: '/pricing' },
    ],
  },
}

const columns = [footerLinks.platform, footerLinks.solutions, footerLinks.company]

const SOCIAL: Array<{ network: SocialNetwork; label: string }> = [
  { network: 'linkedin', label: 'LinkedIn' },
  { network: 'x', label: 'X' },
  { network: 'youtube', label: 'YouTube' },
  { network: 'facebook', label: 'Facebook' },
  { network: 'github', label: 'GitHub' },
]

interface LandingFooterProps {
  isDark: boolean
  onThemeToggle: () => void
}

export function LandingFooter({ isDark, onThemeToggle }: LandingFooterProps) {
  // Only accounts that have an address in contactInfo.social are shown.
  const social = SOCIAL.filter(({ network }) => Boolean(contactInfo.social[network]))
  const { pathname } = useLocation()
  return (
    <footer className="relative overflow-hidden bg-white dark:bg-[#0a0a0f] border-t border-[#ececec] dark:border-[#292929]">
      {/* Faint pink halftone with the same pointer halo as the colour bands
          (reference footer). */}
      <InteractiveDots rgb="255,0,242" intensity={1.25} spacing={12} maxSize={3.4} interactive={pathname === '/'} />

      <div className="landing-container relative z-10 pt-20 pb-12">
        <div className="grid gap-14 lg:grid-cols-[minmax(0,19rem)_minmax(0,1fr)] lg:gap-20">
          {/* Brand column */}
          <div className="flex flex-col items-start">
            <Link to="/" aria-label="Templatean home">
              <LogoWordmark size="lg" />
            </Link>
            <span className="mt-9 mb-6 h-px w-32 bg-[#d8d8d8] dark:bg-[#333]" />
            <p className="text-base leading-snug text-[#363636] dark:text-[#d1d1d1] max-w-[17rem]">
              AI agents that turn your templates, brand rules and business logic into
              documents, delivered on time inside Microsoft Office.
            </p>

            {social.length > 0 && (
              <ul aria-label="Templatean on social media" className="mt-8 flex flex-wrap gap-2">
                {social.map(({ network, label }) => (
                  <li key={network}>
                    <a
                      href={contactInfo.social[network]}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Templatean on ${label}`}
                      className="flex h-10 w-10 items-center justify-center bg-[#0a0a0a]/5 text-[#0a0a0a] transition-colors duration-200 hover:bg-[var(--color-landing-accent)] hover:text-white dark:bg-white/10 dark:text-white dark:hover:bg-[var(--color-landing-accent)]"
                    >
                      <SocialIcon network={network} className="h-[18px] w-[18px]" />
                    </a>
                  </li>
                ))}
              </ul>
            )}

            <button
              type="button"
              onClick={onThemeToggle}
              className="mt-6 inline-flex h-10 items-center gap-2 border border-[#d8d8d8] px-4 text-sm font-semibold text-[#363636] transition-colors duration-200 hover:border-[#0a0a0a] hover:text-[#0a0a0a] dark:border-[#333] dark:text-[#d1d1d1] dark:hover:border-white dark:hover:text-white"
            >
              {isDark ? <Sun aria-hidden className="h-4 w-4" /> : <Moon aria-hidden className="h-4 w-4" />}
              {isDark ? 'Switch to light theme' : 'Switch to dark theme'}
            </button>
          </div>

          {/* Link columns: big uppercase display links, right-aligned */}
          <div className="grid grid-cols-1 gap-12 sm:grid-cols-3 sm:gap-8">
            {columns.map((column) => (
              <div key={column.title} className="sm:text-right">
                <h3 className="sr-only">{column.title}</h3>
                <ul className="flex flex-col gap-3 sm:items-end">
                  {column.links.map((link) => (
                    <li key={link.href}>
                      <Link
                        to={link.href}
                        className="font-[family-name:var(--font-landing-display)] uppercase text-base xl:text-lg leading-[1.3] text-[#0a0a0a] dark:text-white hover:text-[var(--color-landing-accent)] transition-colors duration-200"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-20 flex flex-col md:flex-row items-center justify-between gap-4 pt-8 border-t border-[#d8d8d8] dark:border-[#333]">
          <p className="text-sm text-[#5a5a5a] dark:text-[#ababab]">
            &copy; {new Date().getFullYear()} Dinem Ltd. All rights reserved.
          </p>
          <div className="flex gap-8 text-sm">
            {[
              { label: 'Privacy', href: '/privacy' },
              { label: 'Terms', href: '/terms' },
              { label: 'Security', href: '/platform/security' },
            ].map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="text-[#363636] hover:text-[var(--color-landing-accent)] dark:text-[#d1d1d1] dark:hover:text-white transition-colors duration-200"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Giant brand wordmark spanning the footer, like the reference site. */}
      {/* Clip sideways only: Boldonse's tall caps overflow a tight line box,
          and clipping vertically sliced the tops off the letters. */}
      <div aria-hidden className="relative z-10 select-none overflow-x-clip px-2 pb-4">
        <p className="font-[family-name:var(--font-landing-display)] uppercase text-center whitespace-nowrap leading-[1.35] text-[12.2vw] tracking-[-0.02em] text-[#0a0a0a] dark:text-white">
          Templatean
        </p>
      </div>
    </footer>
  )
}

export default LandingFooter
